<template>
  <v-app id="broker-app">
    <!-- Mobile App Bar -->
    <v-app-bar
      v-if="isAuthenticated && isMobile"
      app
      flat
      color="white"
      height="56"
      class="mobile-app-bar"
    >
      <v-app-bar-nav-icon @click="drawer = !drawer">
        <v-icon>fa-regular fa-bars</v-icon>
      </v-app-bar-nav-icon>
      <v-toolbar-title class="d-flex align-center">
        <v-img
          src="@/assets/Logo_Main.svg"
          max-width="120"
          contain
          class="mr-2"
        />
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="showNotifications = true">
        <v-badge
          :content="notifications"
          :value="notifications"
          color="#28ea96"
          overlap
        >
          <v-icon>fa-regular fa-bell</v-icon>
        </v-badge>
      </v-btn>
    </v-app-bar>

    <!-- Navigation Drawer with transition -->
    <transition name="drawer-slide">
      <v-navigation-drawer
        v-if="isAuthenticated"
        v-model="drawer"
        :mini-variant.sync="miniVariant"
        :expand-on-hover="expandOnHover && !isMobile"
        :temporary="isMobile"
        :permanent="!isMobile"
        app
        dark
        color="black"
        width="280"
        mini-variant-width="68"
        class="broker-navigation"
      >
      <!-- Broker Logo & User Section -->
      <v-list-item class="broker-logo-item nav-list-item">
        <v-list-item-avatar size="32" :tile="true" class="broker-logo-avatar">
          <v-img
            v-if="brokerLogoUrl"
            :src="brokerLogoUrl"
            contain
          />
          <v-icon v-else color="#28ea96" size="24">fa-regular fa-building</v-icon>
        </v-list-item-avatar>
        <v-list-item-content v-if="isDrawerExpanded">
          <v-list-item-title class="user-name-title">
            {{ userName || 'User' }}
          </v-list-item-title>
          <v-list-item-subtitle class="broker-subtitle">
            From {{ brokerName || 'Broker' }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <!-- Navigation Items -->
      <v-list dense nav class="pa-0">
        <v-list-item-group v-model="selectedItem" color="#28ea96">
          <v-list-item
            v-for="item in navigationItems"
            :key="item.title"
            :to="item.disabled ? undefined : item.to"
            :disabled="item.disabled"
            :class="['nav-item nav-list-item', { 'nav-item--disabled': item.disabled }]"
            link
            exact
          >
            <v-list-item-icon>
              <v-tooltip right :disabled="isDrawerExpanded">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" size="20">{{ item.icon }}</v-icon>
                </template>
                <span>{{ item.title }}</span>
              </v-tooltip>
            </v-list-item-icon>
            <v-list-item-content v-if="isDrawerExpanded">
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
      <!-- <v-spacer></v-spacer> -->

      <!-- Bottom Section -->
      <template v-slot:append>
        <v-divider class="mx-3 my-2"></v-divider>
        <div class="bottom-section">
    <!-- Bottom nav items (Members / Servers) -->
          <v-list dense nav class="pa-0">
            <v-list-item-group color="#28ea96">
              <v-list-item
                v-for="item in bottomNavigationItems"
                :key="item.title"
                :to="item.disabled ? undefined : item.to"
                :disabled="item.disabled"
                :class="['nav-item nav-list-item', { 'nav-item--disabled': item.disabled }]"
                link
                exact
              >
                <v-list-item-icon>
                  <v-tooltip right :disabled="isDrawerExpanded">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" size="20">{{ item.icon }}</v-icon>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </v-list-item-icon>
                <v-list-item-content v-if="isDrawerExpanded">
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          <!-- <v-list-item
            @click="openSupport"
            class="support-item nav-list-item"
          >
            <v-list-item-icon>
              <v-tooltip right :disabled="isDrawerExpanded">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" color="#28ea96" size="20">fa-regular fa-question-circle</v-icon>
                </template>
                <span>{{ $t('broker.app.navigation.support') || 'Support' }}</span>
              </v-tooltip>
            </v-list-item-icon>
            <v-list-item-content v-if="isDrawerExpanded">
              <v-list-item-title style="color: #28ea96;">{{ $t('broker.app.navigation.support') || 'Support' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item> -->
          <!-- Language Selector -->
          <v-menu
            v-model="languageMenu"
            offset-y
            :close-on-content-click="false"
            top
          >
            <template v-slot:activator="{ on, attrs }">
              <v-list-item
                v-bind="attrs"
                v-on="on"
                class="language-item nav-list-item"
              >
                <v-list-item-icon>
                  <v-tooltip right :disabled="isDrawerExpanded">
                    <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                      <div v-bind="tooltipAttrs" v-on="tooltipOn" class="flag-icon-container">
                        <Flag
                          :code="currentLanguageFlag"
                          :squared="false"
                        />
                      </div>
                    </template>
                    <span>{{ $t('broker.app.navigation.language') || 'Language' }}</span>
                  </v-tooltip>
                </v-list-item-icon>
                <v-list-item-content v-if="isDrawerExpanded">
                  <v-list-item-title>{{ currentLanguageName }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="isDrawerExpanded">
                  <v-icon size="16">fa-regular fa-chevron-{{ languageMenu ? 'up' : 'down' }}</v-icon>
                </v-list-item-action>
              </v-list-item>
            </template>
            <v-list dense>
              <v-list-item
                v-for="lang in availableLanguages"
                :key="lang.code"
                @click="changeLanguage(lang.code)"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <Flag :code="lang.flag" :squared="false" class="mr-2" style="display: inline-block; vertical-align: middle;" />
                    {{ lang.name }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="currentLanguage === lang.code">
                  <v-icon color="primary" small>fa-regular fa-check</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-list-item
            @click="logout"
            class="logout-item nav-list-item"
          >
            <v-list-item-icon>
              <v-tooltip right :disabled="isDrawerExpanded">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" size="20">fa-regular fa-sign-out-alt</v-icon>
                </template>
                <span>{{ $t('broker.app.navigation.logout') || 'Logout' }}</span>
              </v-tooltip>
            </v-list-item-icon>
            <v-list-item-content v-if="isDrawerExpanded">
              <v-list-item-title>{{ $t('broker.app.navigation.logout') || 'Logout' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </div>
      </template>
      </v-navigation-drawer>
    </transition>

    <!-- Breadcrumbs as simple text component (configurable per route) -->
    <div v-if="isAuthenticated && showBreadcrumb" class="breadcrumb-header" :class="{ 'drawer-open': drawer && !miniVariant, 'mini-drawer': drawer && miniVariant }">
      <div class="breadcrumb-text">
        <span v-for="(item, index) in breadcrumbs" :key="index">
          <span v-if="index > 0" class="mx-1">›</span>
          <span :class="{ 'text--secondary': index < breadcrumbs.length - 1 }">{{ item.text }}</span>
        </span>
      </div>
    </div>

    <!-- Main Content with transition -->
    <v-main class="broker-main">
      <transition name="content-fade">
        <div v-if="isAuthenticated" key="authenticated" class="content-wrapper authenticated" :class="{ 'no-breadcrumb': !showBreadcrumb }">
          <router-view></router-view>
        </div>
        <div v-else key="unauthenticated" class="content-wrapper unauthenticated">
          <router-view></router-view>
        </div>
      </transition>
    </v-main>

    <!-- Notifications Drawer -->
    <v-navigation-drawer
      v-model="showNotifications"
      app
      right
      temporary
      width="400"
    >
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title class="text-h6">
            Notifications
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon @click="showNotifications = false">
            <v-icon>fa-regular fa-times</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
      <v-divider></v-divider>
      <v-list three-line>
        <v-list-item
          v-for="notification in notificationList"
          :key="notification.id"
        >
          <v-list-item-avatar>
            <v-icon :color="notification.color">{{ notification.icon }}</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>{{ notification.title }}</v-list-item-title>
            <v-list-item-subtitle>{{ notification.message }}</v-list-item-subtitle>
            <v-list-item-subtitle class="text-caption">{{ notification.time }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </v-app>
</template>

<script>
import authService from './services/auth.service'
import { getAvailableLocales, setLocale, getLocale } from './i18n'

export default {
  name: 'BrokerApp',
  data() {
    return {
      drawer: false, // Will be set based on screen size
      miniVariant: false,  // Will be set based on screen size
      expandOnHover: false,  // Will be set based on screen size
      isMobile: false,
      selectedItem: 0,
      pageTitle: 'Dashboard',
      darkMode: false,
      currentLanguage: getLocale(),
      languageMenu: false,
      availableLanguages: getAvailableLocales(),
      showNotifications: false,
      notifications: 1,
      userName: '',
      userEmail: '',
      authenticated: false,
      breadcrumbs: [
        { text: 'Broker Portal', disabled: false, href: '/' },
        { text: 'Dashboard', disabled: true }
      ],
      notificationList: [
        {
          id: 1,
          title: 'Welcome to Broker Portal',
          message: 'This is your notification center.',
          time: '5 minutes ago',
          icon: 'fa-regular fa-smile',
          color: 'success'
        },
      ]
    }
  },
  created() {
    // Check screen size before mount
    this.checkScreenSize()
    // Check initial auth state
    this.checkAuthState()
  },
  mounted() {
    this.loadUserInfo()
    this.checkDarkMode()
    this.updateBreadcrumbs()
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  computed: {
    navigationItems() {
      return [
                {
          title: this.$t('broker.app.navigation.home') || 'Home',
          icon: 'fa-regular fa-home',
          to: '/'
        },
        {
          title: this.$t('broker.app.navigation.traders') || 'Traders',
          icon: 'fa-regular fa-users',
          to: '/clubs'
        },
        {
          title: this.$t('broker.app.navigation.competitions') || 'Competitions',
          icon: 'fa-regular fa-trophy',
          to: '/competitions'
        },
        {
          title: this.$t('broker.app.navigation.leagues') || 'Leagues',
          icon: 'fa-regular fa-swords',
          to: '/leagues'
        },  
                {
          title: this.$t('broker.app.navigation.oneVsOne') || 'one vs one',
          icon: 'fa-regular fa-boxing-glove',
          to: '/onevsone'
        },
        {
          title: this.$t('broker.app.navigation.quests') || 'Quests',
          icon: 'fa-regular fa-list-check',
          to: '/quests'
        },

      ]
    },
    bottomNavigationItems() {
      return [
        {
          title: this.$t('broker.app.navigation.members') || 'Members',
          icon: 'fa-regular fa-user',
          to: '/members',
          disabled: this.isReadOnlyStaff
        },
        {
          title: this.$t('broker.app.navigation.servers') || 'Servers & Groups',
          icon: 'fa-regular fa-server',
          to: '/servers'
        },
      ]
    },
    isAuthenticated() {
      return this.authenticated
    },
    showBreadcrumb() {
      // Configure which routes should show the breadcrumb
      // By default, hide breadcrumb unless explicitly enabled
      const routesWithBreadcrumb = [
        // Add route paths here when you want to show breadcrumb
        // Example: '/dashboard', '/profile', '/settings'
      ]
      return routesWithBreadcrumb.includes(this.$route.path)
    },
    isDrawerExpanded() {
      // Computed property to check if drawer is truly expanded
      return !this.miniVariant
    },
    currentLanguageName() {
      const lang = this.availableLanguages.find(l => l.code === this.currentLanguage.toLowerCase())
      return lang ? lang.name : 'English'
    },
    currentLanguageFlag() {
      // Normalize the current language code
      const normalizedCode = this.currentLanguage ? this.currentLanguage.toLowerCase() : 'en'
      const lang = this.availableLanguages.find(l => l.code === normalizedCode)
      return lang ? lang.flag : 'US'
    },
    userProfilePicture() {
      const user = this.$store.getters['broker/currentUser']
      return user?.imageUrl || null
    },
    brokerLogoUrl() {
      return this.$store.getters['broker/brokerLogoUrl']
    },
    brokerName() {
      return this.$store.getters['broker/brokerName']
    },
    isReadOnlyStaff() {
      return this.$store.getters['broker/isReadOnlyStaff']
    },
  },
  watch: {
    $route(to) {
      // Check auth state on route change
      this.checkAuthState()

      // Update page title and breadcrumbs based on route
      const item = this.navigationItems.find(item => item.to === to.path)
      this.pageTitle = item ? item.title : 'Broker Portal'
      this.updateBreadcrumbs()

      // Update selected navigation item
      const index = this.navigationItems.findIndex(item => item.to === to.path)
      if (index >= 0) {
        this.selectedItem = index
      }
    },

    // Watch for authentication state changes
    authenticated(newVal) {
      if (newVal) {
        // Force drawer to properly initialize when user logs in
        this.$nextTick(() => {
          // Reset drawer state to ensure proper rendering
          this.drawer = false
          this.miniVariant = !this.isMobile // Only mini on desktop
          this.expandOnHover = !this.isMobile // Only expand on hover for desktop

          // Re-enable drawer after a tick
          this.$nextTick(() => {
            this.drawer = !this.isMobile // Keep closed on mobile initially
          })
        })
      }
    },
    isMobile(newVal) {
      // Adjust drawer behavior when screen size changes
      if (newVal) {
        this.drawer = false
        this.miniVariant = false
        this.expandOnHover = false
      } else {
        this.drawer = true
        this.miniVariant = true
        this.expandOnHover = true
      }
    }
  },
  methods: {
    async checkAuthState() {
      // Check auth state from store/localStorage
      const isAuthenticated = await authService.checkAuthState()
      this.authenticated = isAuthenticated
      if (this.authenticated) {
        this.loadUserInfo()
        this.loadBrokerInfo()
      }
    },
    loadUserInfo() {
      const user = authService.getCurrentUser()
      if (user) {
        // For now, use username since we don't have firstName/lastName yet
        this.userName = user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.username
        this.userEmail = user.email || user.username
      }
    },
    async loadBrokerInfo() {
      try {
        await this.$store.dispatch('broker/fetchBrokerInfo')
      } catch (error) {
        console.error('Failed to load broker info:', error)
      }
    },
    updateBreadcrumbs() {
      const item = this.navigationItems.find(item => item.to === this.$route.path)
      if (item) {
        this.breadcrumbs = [
          { text: 'Broker Portal', disabled: false, href: '/' },
          { text: item.title, disabled: true }
        ]
      }
    },
    toggleTheme() {
      this.darkMode = !this.darkMode
      this.$vuetify.theme.dark = this.darkMode
      localStorage.setItem('broker_theme', this.darkMode ? 'dark' : 'light')
    },
    checkDarkMode() {
      const theme = localStorage.getItem('broker_theme')
      if (theme === 'dark') {
        this.darkMode = true
        this.$vuetify.theme.dark = true
      }
    },
    changeLanguage(code) {
      this.currentLanguage = code.toUpperCase()
      if (this.$i18n) {
        this.$i18n.locale = code
      }
      localStorage.setItem('broker_language', code)
    },
    toggleDrawer() {
      if (this.drawer && !this.miniVariant) {
        // If drawer is open and not mini, make it mini
        this.miniVariant = true
      } else if (this.drawer && this.miniVariant) {
        // If drawer is mini, close it completely
        this.drawer = false
        this.miniVariant = false
      } else {
        // If drawer is closed, open it in full mode
        this.drawer = true
        this.miniVariant = false
      }
    },
    profile() {
      // Profile navigation blocked - component not yet implemented
      // this.$router.push('/profile')
    },
    settings() {
      this.$router.push('/settings')
    },
    openSupport() {
      // Open support portal in a new tab
      window.open('https://swiset.atlassian.net/servicedesk/customer/portal/1', '_blank')
    },
    async logout() {
      // Clear auth data
      await authService.logout()
      // Update auth state
      this.authenticated = false
      // Redirect to login
      this.$router.push('/login')
    },
    changeLanguage(langCode) {
      setLocale(langCode)
      this.currentLanguage = langCode
      this.languageMenu = false
      // Force re-render of navigation items to update translations
      this.$forceUpdate()
    },
    checkScreenSize() {
      // Check if screen width is below Vuetify's md breakpoint (960px)
      const wasMobile = this.isMobile
      this.isMobile = window.innerWidth < 960

      // Set initial drawer state on first load
      if (wasMobile === undefined || wasMobile !== this.isMobile) {
        if (this.isMobile) {
          this.drawer = false
          this.miniVariant = false
          this.expandOnHover = false
        } else if (this.isAuthenticated) {
          this.drawer = true
          this.miniVariant = true
          this.expandOnHover = true
        }
      }
    }
  }
}
</script>

<style lang="scss">
// Global CSS variables for broker app
:root {
  --broker-primary: #28ea96 !important;
  --broker-secondary: #20a070 !important;
  --broker-accent: #FFC123 !important;
}

// Override for v-application to ensure it uses our colors
.v-application {
  --v-primary-base: #28ea96 !important;
}

// Ensure all broker styles are scoped within .broker-app
.broker-app {
  // Use consistent font family
  font-family: 'Avenir', sans-serif !important;

  #broker-app {
    font-family: 'Avenir', sans-serif;

    .broker-navigation {
      background-color: #000000 !important;

      // Override theme to keep it black
      &.theme--dark,
      &.theme--light {
        background-color: #000000 !important;
      }

      .nav-item--disabled {
        opacity: 0.4;
        pointer-events: none;
        cursor: not-allowed;
      }
      // Universal styling for all nav list items
      .nav-list-item {
        min-height: 48px !important;
        height: 48px !important;
        margin: 4px 8px !important;
        padding: 0 12px !important;
        border-radius: 8px;
        transition: background-color 0.2s ease;
        display: flex !important;
        align-items: center !important;

        &:hover {
          background-color: rgba(40, 234, 150, 0.1) !important;
        }

        // Icon container standardization
        .v-list-item__icon {
          margin: 0 !important;
          margin-right: 16px !important;
          min-width: 20px !important;
          width: 20px !important;
          height: 20px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          align-self: center !important;

          .v-icon {
            font-size: 16px !important;
            width: 16px !important;
            height: 16px !important;
            line-height: 1 !important;
          }

          // Flag icon container
          .flag-icon-container {
            width: 20px !important;
            height: 20px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;

            img {
              width: 20px !important;
              height: auto !important;
              display: block !important;
            }
          }
        }

        // Avatar specific styling
        .v-list-item__avatar {
          min-width: 32px !important;
          width: 32px !important;
          height: 32px !important;
          margin: 0 !important;
          margin-right: 16px !important;
          align-self: center !important;
        }

        // Content standardization
        .v-list-item__content {
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          align-self: center !important;

          .v-list-item__title {
            font-size: 14px !important;
            font-weight: 400 !important;
            letter-spacing: normal !important;
            line-height: 20px !important;
            color: #f8f8f8 !important;
            margin-left: 20px;
          }
        }

        // Action icon (chevron in language selector)
        .v-list-item__action {
          margin: 0 !important;
          margin-left: auto !important;
          min-width: 24px !important;
          align-self: center !important;

          .v-icon {
            font-size: 16px !important;
          }
        }

        // Active state for navigation items
        &.v-list-item--active {
          background-color: rgba(40, 234, 150, 0.2) !important;

          .v-icon {
            color: #28ea96 !important;
          }

          .v-list-item__title {
            color: #28ea96 !important;
            font-weight: 500 !important;
          }
        }
      }

      // Broker logo item specific (very top)
      .broker-logo-item {
        margin-top: 16px !important;
        margin-bottom: 16px !important;

        .broker-logo-avatar {
          border-radius: 4px !important;
          overflow: hidden;
          background: transparent;
        }

        .v-list-item__content {
          margin-left: 0 !important;

          .v-list-item__title.user-name-title {
            color: #f8f8f8 !important;
            font-weight: 600 !important;
            font-size: 14px !important;
            line-height: 1.3 !important;
            margin-left: 0 !important;
          }

          .v-list-item__subtitle.broker-subtitle {
            color: #ABABAB !important;
            font-weight: 400 !important;
            font-size: 12px !important;
            line-height: 1.3 !important;
            margin-top: 2px !important;
            margin-left: 0 !important;
          }
        }
      }

      // Support item title color override
      .support-item {
        .v-list-item__title {
          color: #28ea96 !important;
        }
      }

      // Bottom section container
      .bottom-section {
        padding: 12px 0;
      }

      // Mini variant specific adjustments
      &.v-navigation-drawer--mini-variant {
        .nav-list-item {
          margin: 4px 0 !important;
          padding: 0 !important;
          justify-content: center !important;

          .v-list-item__icon {
            margin: 0 !important;
          }

          .v-list-item__avatar {
            margin: 0 !important;
          }
        }

        // Restore broker logo margin in mini mode
        .broker-logo-item {
          margin-top: 16px !important;
          margin-bottom: 16px !important;
        }

        .bottom-section {
          padding: 12px 0;
        }
      }
    }

    // Breadcrumb header styling
    .breadcrumb-header {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 60px;
      padding: 0 24px;
      background-color: #f8f9fa; // Match with main app background
      display: flex;
      align-items: center;
      z-index: 4;
      transition: padding-left 0.3s;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);

      &.drawer-open {
        padding-left: 304px; // 280px drawer + 24px padding
      }

      &.mini-drawer {
        padding-left: 128px; // 80px mini drawer + 24px padding
      }

      .breadcrumb-text {
        font-size: 14px;
        color: #616161;

        .text--secondary {
          color: #9e9e9e;
        }
      }
    }

    // Main content styles
    .v-main.broker-main {
      background-color: #f8f9fa;
      min-height: 100vh;

      .content-wrapper {
        max-width: 100%;

        // Authenticated routes (with sidebar and breadcrumbs)
        &.authenticated {
          padding: 120px 48px 24px 48px;

          // When breadcrumb is hidden, reduce top padding
          &.no-breadcrumb {
            padding-top: 48px;
          }
          &.no-breadcrumb:has(.wizard-modal-backdrop) {
            padding: 0px !important;
          }
        }

        // Unauthenticated routes (login page - no padding)
        &.unauthenticated {
          padding: 0;
        }
      }
    }

    // Theme customization
    .v-application {
      // Primary color for broker portal
      --broker-primary: #28ea96;

      .primary {
        background-color: var(--broker-primary) !important;
        border-color: var(--broker-primary) !important;
      }

      .primary--text {
        color: var(--broker-primary) !important;
        caret-color: var(--broker-primary) !important;
      }
    }

    // Custom scrollbar
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;

      &:hover {
        background: #555;
      }
    }

    &.sidebar-divider {
      margin: 8px 16px;
      opacity: 0.9;
    }

    // Card styling
    .v-card {
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    // Mobile app bar styles
    .mobile-app-bar {
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);

      .v-toolbar__title {
        font-size: 16px;
      }
    }

    // Responsive adjustments
    @media (max-width: 960px) {
      .v-main.broker-main {
        .content-wrapper {
          &.authenticated {
            padding: 72px 16px 16px 16px; // 56px app bar + 16px padding
          }

          &.unauthenticated {
            padding: 0;
          }
        }
      }

      .breadcrumb-header {
        display: none; // Hide breadcrumbs on mobile
      }

      // Navigation drawer on mobile
      .broker-navigation {
        // Remove hover effects on mobile
        .nav-item {
          &:hover {
            background-color: transparent;
          }

          &.v-list-item--active {
            background-color: rgba(40, 234, 150, 0.2);
          }
        }
      }
    }

    // Tablet adjustments
    @media (min-width: 600px) and (max-width: 959px) {
      .v-main.broker-main {
        .content-wrapper {
          &.authenticated {
            padding: 72px 24px 24px 24px;
          }
        }
      }
    }
  }
}

// Login transition animations
.drawer-slide-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-slide-enter {
  transform: translateX(-100%);
  opacity: 0;
}

.drawer-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

.drawer-slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

// Content fade transition
.content-fade-enter-active {
  transition: all 0.4s ease;
}

.content-fade-leave-active {
  // No leave transition to prevent flash of default states during unmount
  transition: none;
}

.content-fade-enter {
  opacity: 0;
}

.content-fade-leave-to {
  opacity: 0;
}

// Add a subtle animation when drawer first appears after login
.broker-navigation {
  &.v-navigation-drawer--open {
    animation: drawer-init 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

@keyframes drawer-init {
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

// Smooth transition for logo changes
.logo-item {
  .v-list-item__avatar,
  .v-list-item__content {
    transition: all 0.3s ease;
  }
}
</style>